import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from "react";
import { History, MessageSquarePlus, Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";
import { useUserOptional } from "@/auth/UserContext";
import { useKaiWelcomePrompts } from "@/hooks/useKaiWelcomePrompts";
import { useRightRail } from "@/context/RightRailContext";
import { firstName, resolveDisplayName } from "@/lib/userDisplay";
import { Button } from "@/components/ui/button";
import { useKaiAttachments } from "@/hooks/useKaiAttachments";
import { useKaiFileDrop } from "@/hooks/useKaiFileDrop";
import { useMainScrollEdge } from "@/hooks/useMainScrollEdge";
import { useKaiSlashCommands } from "@/hooks/useKaiSlashCommands";
import { useKaiChat } from "./KaiChatContext";
import { KaiComposer } from "./KaiComposer";
import { KaiConversationList } from "./KaiConversationList";
import { KaiFileDropOverlay } from "./KaiFileDropOverlay";
import type { KaiEntityTag } from "./kaiEntityTags";
import type { KaiSlashSelection } from "./kaiTypes";
import {
  KaiFollowUps,
  KaiLiveTurn,
  KaiThreadItems,
} from "./KaiLiveTurn";

/** Rail card inner scrollport with top/bottom content dissolve (Kai chat, insight, …). */
export function RailFadeScroller({
  scrollRef,
  className,
  children,
  underRailHeader = true,
  contentMask = true,
}: {
  scrollRef: Ref<HTMLDivElement>;
  className?: string;
  children: ReactNode;
  /** Fade spans the collapsible card title row (Kai chat). Off when a sub-header sits above the scroller. */
  underRailHeader?: boolean;
  /** Off when the scrollport has interactive controls at the top (mask alpha blocks clicks). */
  contentMask?: boolean;
}) {
  return (
    <div
      className={cn(
        "scroll-fade-frame scroll-fade-frame--rail relative flex min-h-0 flex-1 flex-col",
        contentMask && "scroll-fade-frame--content-mask",
        underRailHeader && "scroll-fade-frame--under-rail-header",
      )}
    >
      <div ref={scrollRef} className={className}>
        {children}
      </div>
    </div>
  );
}

/** History + new-chat controls for the Kai rail card header. */
export function KaiSidePanelActions() {
  const kai = useKaiChat();
  const { kaiSectionOpen, toggleKaiSection } = useRightRail();
  if (!kai) return null;

  const isFreshChat = kai.isWelcome && !kai.isGenerating;

  const ensureExpanded = () => {
    if (!kaiSectionOpen) toggleKaiSection();
  };

  return (
    <div className="flex shrink-0 items-center">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          "size-7 rounded-lg",
          kai.historyOpen
            ? "bg-primary/15 text-primary"
            : "text-muted-foreground hover:text-foreground",
        )}
        aria-label={kai.historyOpen ? "Back to chat" : "Chat history"}
        title={kai.historyOpen ? "Back to chat" : "Chat history"}
        aria-pressed={kai.historyOpen}
        onClick={() => {
          if (!kai.historyOpen) ensureExpanded();
          kai.toggleHistory();
        }}
      >
        <History className="size-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7 rounded-lg text-muted-foreground hover:text-foreground"
        aria-label="New chat"
        title="New chat"
        disabled={isFreshChat && !kai.historyOpen}
        onClick={() => {
          ensureExpanded();
          kai.startNewChat();
        }}
      >
        <MessageSquarePlus className="size-3.5" />
      </Button>
    </div>
  );
}

/** Compact Kai chat for the right rail side card. */
export function KaiSidePanel({ className }: { className?: string }) {
  const kai = useKaiChat();
  const userCtx = useUserOptional();
  const name = firstName(
    resolveDisplayName(
      userCtx?.profile?.name ?? userCtx?.user?.name,
      userCtx?.profile?.email ?? userCtx?.user?.email,
    ),
  );
  const [draft, setDraft] = useState("");
  const [entityTags, setEntityTags] = useState<KaiEntityTag[]>([]);
  const [, setSlashSelection] = useState<KaiSlashSelection | null>(null);
  const slashCommands = useKaiSlashCommands();
  const showHistory = kai?.historyOpen ?? false;
  const isWelcome = kai?.isWelcome ?? true;
  const activeId = kai?.activeId ?? null;
  const scrollRef = useMainScrollEdge<HTMLDivElement>(
    showHistory ? "history" : isWelcome ? "welcome" : `thread:${activeId}`,
  );
  const welcomePrompts = useKaiWelcomePrompts(isWelcome);

  const isGenerating = kai?.isGenerating ?? false;
  const sendMessage = kai?.sendMessage;
  const streamingContent = kai?.streamingContent ?? "";
  const toolCount = kai?.toolActivity.length ?? 0;
  const awaitingInput = kai?.awaitingClarification ?? false;
  const clarificationCount = kai?.pendingClarifications.length ?? 0;
  const ensureConversation = kai?.ensureConversation;

  const resolveConversationId = useCallback(
    () => (ensureConversation ? ensureConversation() : Promise.resolve(null)),
    [ensureConversation],
  );
  const attachments = useKaiAttachments({ resolveConversationId });
  const clearAttachments = attachments.clear;
  const { isDragging, dropHandlers } = useKaiFileDrop({
    onFiles: attachments.addFiles,
    disabled: isGenerating,
  });

  const handleSend = useCallback(
    (
      text: string,
      tags: KaiEntityTag[],
      attachmentIds: string[],
      slash: KaiSlashSelection | null,
    ) => {
      if (!sendMessage) return;
      sendMessage(
        text,
        tags,
        attachmentIds,
        attachments.snapshotMetadata(),
        slash,
      );
      attachments.clear();
      setSlashSelection(null);
    },
    [sendMessage, attachments],
  );

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!isWelcome && !showHistory) scrollToBottom();
  }, [
    kai?.thread.length,
    isGenerating,
    streamingContent,
    toolCount,
    clarificationCount,
    isWelcome,
    showHistory,
    scrollToBottom,
  ]);

  useEffect(() => {
    if (isWelcome) {
      setDraft("");
      setEntityTags([]);
      setSlashSelection(null);
      clearAttachments();
    }
  }, [isWelcome, clearAttachments]);

  // Clear pending files when switching to a different existing conversation.
  const prevActiveIdRef = useRef<string | null>(activeId);
  useEffect(() => {
    const prev = prevActiveIdRef.current;
    if (prev !== activeId && prev !== null) clearAttachments();
    prevActiveIdRef.current = activeId;
  }, [activeId, clearAttachments]);

  if (!kai || !sendMessage) return null;

  if (showHistory) {
    return (
      <div
        className={cn(
          "flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden",
          className,
        )}
      >
        <RailFadeScroller
          scrollRef={scrollRef}
          className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain scroll-pt-[var(--scroll-edge-top-range)]"
        >
          <KaiConversationList
            conversations={kai.conversations}
            activeId={kai.activeId}
            onSelect={kai.selectConversation}
            onNewChat={kai.startNewChat}
            showNewChat={false}
            className="min-h-full min-w-0"
            listClassName="overflow-visible pb-[var(--scroll-edge-bottom-fade-height)]"
          />
        </RailFadeScroller>
      </div>
    );
  }

  return (
    <div
      {...dropHandlers}
      className={cn(
        "kai-rail-panel relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden",
        className,
      )}
    >
      {isDragging && <KaiFileDropOverlay />}
      <RailFadeScroller
        scrollRef={scrollRef}
          className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain [overflow-anchor:none] scroll-pt-[var(--scroll-edge-top-range)]"
      >
        {isWelcome ? (
          <div className="flex min-h-full flex-col gap-3 pb-[var(--scroll-edge-bottom-fade-height)] pt-[var(--scroll-edge-top-range)]">
            <div className="rounded-xl border border-border/50 bg-foreground/[0.03] p-3 text-center">
              <div className="mx-auto mb-2 flex size-9 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <Sparkles className="size-4" />
              </div>
              <p className="text-sm font-medium">Hi {name}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Ask about bookings, rates, or rebook ops.
              </p>
            </div>
            <div
              className={cn(
                "grid gap-1.5 transition-opacity",
                welcomePrompts.loading && "opacity-60",
              )}
            >
              {welcomePrompts.prompts.slice(0, 4).map((prompt) => (
                <button
                  key={prompt.id}
                  type="button"
                  onClick={() => sendMessage(prompt.text)}
                  className="rounded-lg border border-border/50 bg-foreground/[0.03] px-3 py-2 text-left text-xs transition-colors hover:bg-accent/40"
                >
                  {prompt.text}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex min-h-full min-w-0 flex-col">
            <div className="mt-auto flex min-w-0 flex-col gap-3 pb-[var(--scroll-edge-bottom-fade-height)] pr-1 pt-2">
              <KaiThreadItems />
              <KaiLiveTurn />
              {kai.error && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/8 px-3 py-2 text-xs text-destructive">
                  {kai.error}
                </div>
              )}
              {!isGenerating && (
                <KaiFollowUps
                  suggestions={kai.followUpSuggestions}
                  onSelect={sendMessage}
                />
              )}
            </div>
          </div>
        )}
      </RailFadeScroller>

      <div className="relative z-20 shrink-0 pt-1">
        <KaiComposer
          value={draft}
          onChange={setDraft}
          onSend={handleSend}
          onStop={kai.stopGeneration}
          disabled={isGenerating && !awaitingInput}
          isGenerating={isGenerating}
          awaitingInput={awaitingInput}
          entityTags={entityTags}
          onEntityTagsChange={setEntityTags}
          slashCommands={slashCommands}
          onSlashCommandChange={setSlashSelection}
          attachmentEntries={attachments.entries}
          onAddFiles={attachments.addFiles}
          onRemoveAttachment={attachments.removeAt}
          attachmentsBlocking={attachments.blocking}
          placeholder="Ask Kai…  ·  @ tag  ·  / commands"
          className="rounded-xl p-1.5"
        />
      </div>
    </div>
  );
}
